import type { LatLon } from './types.ts';

/*
 * Geodesy for display and production awareness (NOT navigation).
 *
 * - Distances/bearings/destinations: Vincenty on WGS84 (sub-millimetre for our ranges), with a
 *   spherical fallback for the rare antipodal non-convergence.
 * - Slant range and elevation angle: exact geometric ECEF vectors on WGS84 (no atmospheric
 *   refraction; heights are treated as ellipsoidal ≈ MSL, which is fine for look angles).
 * - Relative motion (closing speed, CPA): flat local east/north frame centred between the two
 *   bodies; horizontal only (vertical speed is ignored). Accurate for the ≤ ~100 km ranges we use.
 */

export const M_PER_NM = 1852;
export const M_PER_FT = 0.3048;
export const MPS_PER_KT = 1852 / 3600;
export const M_PER_SM = 1609.344;

/** WGS84 */
export const WGS84_A = 6378137.0;
export const WGS84_F = 1 / 298.257223563;
export const WGS84_B = WGS84_A * (1 - WGS84_F);
/** Mean earth radius (IUGG), metres. */
export const EARTH_MEAN_RADIUS_M = 6371008.8;

const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;

/** Normalise any angle to [0,360). */
export function normalizeDeg(d: number): number {
  if (!Number.isFinite(d)) return NaN;
  const r = ((d % 360) + 360) % 360;
  return r >= 360 || Object.is(r, -0) ? 0 : r;
}

/** Signed smallest difference to−from, in [-180,180). */
export function angleDiffDeg(from: number, to: number): number {
  const d = normalizeDeg(to - from);
  return d >= 180 ? d - 360 : d;
}

/** Great-circle (haversine) distance on the mean sphere, metres. */
export function haversineM(a: LatLon, b: LatLon): number {
  const φ1 = a.lat * D2R;
  const φ2 = b.lat * D2R;
  const dφ = φ2 - φ1;
  const dλ = (b.lon - a.lon) * D2R;
  const h = Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  return 2 * EARTH_MEAN_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

function sphericalBearingDeg(a: LatLon, b: LatLon): number {
  const φ1 = a.lat * D2R;
  const φ2 = b.lat * D2R;
  const dλ = (b.lon - a.lon) * D2R;
  const y = Math.sin(dλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(dλ);
  return normalizeDeg(Math.atan2(y, x) * R2D);
}

interface VincentyInverse {
  distanceM: number;
  initialBearingDeg: number;
  finalBearingDeg: number;
}

/** Vincenty inverse on WGS84; null on non-convergence (nearly antipodal points). */
export function vincentyInverse(a: LatLon, b: LatLon): VincentyInverse | null {
  const f = WGS84_F;
  const aa = WGS84_A;
  const bb = WGS84_B;
  const L = angleDiffDeg(a.lon, b.lon) * D2R;
  const U1 = Math.atan((1 - f) * Math.tan(a.lat * D2R));
  const U2 = Math.atan((1 - f) * Math.tan(b.lat * D2R));
  const sinU1 = Math.sin(U1);
  const cosU1 = Math.cos(U1);
  const sinU2 = Math.sin(U2);
  const cosU2 = Math.cos(U2);

  let λ = L;
  let sinλ = 0;
  let cosλ = 0;
  let sinσ = 0;
  let cosσ = 0;
  let σ = 0;
  let cosSqα = 0;
  let cos2σm = 0;
  let iter = 0;
  let λPrev: number;
  do {
    sinλ = Math.sin(λ);
    cosλ = Math.cos(λ);
    const t1 = cosU2 * sinλ;
    const t2 = cosU1 * sinU2 - sinU1 * cosU2 * cosλ;
    sinσ = Math.sqrt(t1 * t1 + t2 * t2);
    if (sinσ === 0) return { distanceM: 0, initialBearingDeg: 0, finalBearingDeg: 0 }; // coincident
    cosσ = sinU1 * sinU2 + cosU1 * cosU2 * cosλ;
    σ = Math.atan2(sinσ, cosσ);
    const sinα = (cosU1 * cosU2 * sinλ) / sinσ;
    cosSqα = 1 - sinα * sinα;
    cos2σm = cosSqα !== 0 ? cosσ - (2 * sinU1 * sinU2) / cosSqα : 0; // equatorial line
    const C = (f / 16) * cosSqα * (4 + f * (4 - 3 * cosSqα));
    λPrev = λ;
    λ = L + (1 - C) * f * sinα * (σ + C * sinσ * (cos2σm + C * cosσ * (-1 + 2 * cos2σm * cos2σm)));
    if (Math.abs(λ) > Math.PI) return null;
  } while (Math.abs(λ - λPrev) > 1e-12 && ++iter < 200);
  if (iter >= 200) return null;

  const uSq = (cosSqα * (aa * aa - bb * bb)) / (bb * bb);
  const A = 1 + (uSq / 16384) * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)));
  const B = (uSq / 1024) * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)));
  const Δσ =
    B *
    sinσ *
    (cos2σm +
      (B / 4) *
        (cosσ * (-1 + 2 * cos2σm * cos2σm) - (B / 6) * cos2σm * (-3 + 4 * sinσ * sinσ) * (-3 + 4 * cos2σm * cos2σm)));
  const s = bb * A * (σ - Δσ);
  const α1 = Math.atan2(cosU2 * sinλ, cosU1 * sinU2 - sinU1 * cosU2 * cosλ);
  const α2 = Math.atan2(cosU1 * sinλ, -sinU1 * cosU2 + cosU1 * sinU2 * cosλ);
  return { distanceM: s, initialBearingDeg: normalizeDeg(α1 * R2D), finalBearingDeg: normalizeDeg(α2 * R2D) };
}

/** WGS84 ellipsoidal distance (Vincenty inverse; falls back to haversine on non-convergence). Metres. */
export function distanceM(a: LatLon, b: LatLon): number {
  if (a.lat === b.lat && a.lon === b.lon) return 0;
  const v = vincentyInverse(a, b);
  return v ? v.distanceM : haversineM(a, b);
}

/** Initial great-circle bearing a→b, degrees true [0,360). */
export function bearingDeg(a: LatLon, b: LatLon): number {
  if (a.lat === b.lat && a.lon === b.lon) return 0;
  const v = vincentyInverse(a, b);
  return v ? v.initialBearingDeg : sphericalBearingDeg(a, b);
}

/** Point reached from `a` travelling `distM` on initial bearing `brgDeg` (Vincenty direct on WGS84). */
export function destinationPoint(a: LatLon, brgDeg: number, distM: number): LatLon {
  if (distM === 0) return { lat: a.lat, lon: a.lon };
  const f = WGS84_F;
  const aa = WGS84_A;
  const bb = WGS84_B;
  const α1 = brgDeg * D2R;
  const sinα1 = Math.sin(α1);
  const cosα1 = Math.cos(α1);
  const tanU1 = (1 - f) * Math.tan(a.lat * D2R);
  const cosU1 = 1 / Math.sqrt(1 + tanU1 * tanU1);
  const sinU1 = tanU1 * cosU1;
  const σ1 = Math.atan2(tanU1, cosα1);
  const sinα = cosU1 * sinα1;
  const cosSqα = 1 - sinα * sinα;
  const uSq = (cosSqα * (aa * aa - bb * bb)) / (bb * bb);
  const A = 1 + (uSq / 16384) * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)));
  const B = (uSq / 1024) * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)));

  let σ = distM / (bb * A);
  let σPrev: number;
  let sinσ = 0;
  let cosσ = 0;
  let cos2σm = 0;
  let iter = 0;
  do {
    cos2σm = Math.cos(2 * σ1 + σ);
    sinσ = Math.sin(σ);
    cosσ = Math.cos(σ);
    const Δσ =
      B *
      sinσ *
      (cos2σm +
        (B / 4) *
          (cosσ * (-1 + 2 * cos2σm * cos2σm) - (B / 6) * cos2σm * (-3 + 4 * sinσ * sinσ) * (-3 + 4 * cos2σm * cos2σm)));
    σPrev = σ;
    σ = distM / (bb * A) + Δσ;
  } while (Math.abs(σ - σPrev) > 1e-12 && ++iter < 100);

  const x = sinU1 * sinσ - cosU1 * cosσ * cosα1;
  const φ2 = Math.atan2(sinU1 * cosσ + cosU1 * sinσ * cosα1, (1 - f) * Math.sqrt(sinα * sinα + x * x));
  const λ = Math.atan2(sinσ * sinα1, cosU1 * cosσ - sinU1 * sinσ * cosα1);
  const C = (f / 16) * cosSqα * (4 + f * (4 - 3 * cosSqα));
  const L = λ - (1 - C) * f * sinα * (σ + C * sinσ * (cos2σm + C * cosσ * (-1 + 2 * cos2σm * cos2σm)));
  let lon = a.lon + L * R2D;
  lon = ((((lon + 180) % 360) + 360) % 360) - 180;
  return { lat: φ2 * R2D, lon };
}

/** Great-circle interpolation (spherical slerp), f in [0,1]. */
export function interpolateGreatCircle(a: LatLon, b: LatLon, f: number): LatLon {
  if (f <= 0) return { lat: a.lat, lon: a.lon };
  if (f >= 1) return { lat: b.lat, lon: b.lon };
  const φ1 = a.lat * D2R;
  const λ1 = a.lon * D2R;
  const φ2 = b.lat * D2R;
  const λ2 = b.lon * D2R;
  const ax = Math.cos(φ1) * Math.cos(λ1);
  const ay = Math.cos(φ1) * Math.sin(λ1);
  const az = Math.sin(φ1);
  const bx = Math.cos(φ2) * Math.cos(λ2);
  const by = Math.cos(φ2) * Math.sin(λ2);
  const bz = Math.sin(φ2);
  const dot = Math.max(-1, Math.min(1, ax * bx + ay * by + az * bz));
  const δ = Math.acos(dot);
  if (δ < 1e-12) {
    // Coincident (or numerically so): linear is exact enough.
    return { lat: a.lat + (b.lat - a.lat) * f, lon: a.lon + angleDiffDeg(a.lon, b.lon) * f };
  }
  const sδ = Math.sin(δ);
  const A = Math.sin((1 - f) * δ) / sδ;
  const B = Math.sin(f * δ) / sδ;
  const x = A * ax + B * bx;
  const y = A * ay + B * by;
  const z = A * az + B * bz;
  return { lat: Math.atan2(z, Math.sqrt(x * x + y * y)) * R2D, lon: Math.atan2(y, x) * R2D };
}

/** ECEF (metres) of a geodetic position with height in metres above the ellipsoid. */
export function toEcef(p: LatLon, hM: number): [number, number, number] {
  const φ = p.lat * D2R;
  const λ = p.lon * D2R;
  const e2 = WGS84_F * (2 - WGS84_F);
  const sφ = Math.sin(φ);
  const N = WGS84_A / Math.sqrt(1 - e2 * sφ * sφ);
  return [(N + hM) * Math.cos(φ) * Math.cos(λ), (N + hM) * Math.cos(φ) * Math.sin(λ), (N * (1 - e2) + hM) * sφ];
}

/** 3-D straight-line range given altitudes in metres (exact ECEF chord). */
export function slantRangeM(a: LatLon, altAM: number, b: LatLon, altBM: number): number {
  const p = toEcef(a, altAM);
  const q = toEcef(b, altBM);
  return Math.hypot(q[0] - p[0], q[1] - p[1], q[2] - p[2]);
}

/**
 * Elevation (look-up) angle from observer to target in degrees; accounts for earth curvature
 * geometrically (ECEF line of sight vs. the observer's ellipsoid normal). No refraction.
 */
export function elevationAngleDeg(observer: LatLon, observerAltM: number, target: LatLon, targetAltM: number): number {
  const p = toEcef(observer, observerAltM);
  const q = toEcef(target, targetAltM);
  const d: [number, number, number] = [q[0] - p[0], q[1] - p[1], q[2] - p[2]];
  const len = Math.hypot(d[0], d[1], d[2]);
  if (len === 0) return 0;
  const φ = observer.lat * D2R;
  const λ = observer.lon * D2R;
  const up = [Math.cos(φ) * Math.cos(λ), Math.cos(φ) * Math.sin(λ), Math.sin(φ)];
  const s = (d[0] * up[0]! + d[1] * up[1]! + d[2] * up[2]!) / len;
  return Math.asin(Math.max(-1, Math.min(1, s))) * R2D;
}

/** Bearing relative to own track/heading, [-180,180): +ve = right. */
export function relativeBearingDeg(ownHeadingDeg: number, absBearingDeg: number): number {
  return angleDiffDeg(ownHeadingDeg, absBearingDeg);
}

/** Clock position 1..12 for a relative bearing (12 = dead ahead, 3 = right). */
export function clockPosition(relBearingDeg: number): number {
  const n = Math.round(normalizeDeg(relBearingDeg) / 30) % 12;
  return n === 0 ? 12 : n;
}

export interface Kinematic {
  pos: LatLon;
  /** metres MSL, null if unknown */
  altM: number | null;
  /** knots, null/0 for a stationary object */
  gsKt: number | null;
  /** degrees true */
  trackDeg: number | null;
  /** feet/min */
  vsFpm?: number | null;
}

export type Trend = 'closing' | 'opening' | 'steady' | 'unknown';

export interface RelativeGeometry {
  /** Horizontal (ellipsoidal) distance, metres. */
  distanceM: number;
  distanceNm: number;
  /** from → to, degrees true. */
  bearingDeg: number;
  /** to → from. */
  reciprocalDeg: number;
  /** to.alt − from.alt in feet; null if either unknown. */
  altDiffFt: number | null;
  slantRangeM: number | null;
  /** Positive = closing, negative = opening (knots); null if neither moves / unknown. */
  closingKt: number | null;
  trend: Trend;
  /** Seconds until `to` reaches `from` at current closure (only when closing meaningfully). */
  etaSec: number | null;
  /** Closest point of approach assuming constant velocities; null if no relative motion. */
  cpa: { tSec: number; distanceM: number } | null;
  /** Look-up angle from `from` to `to` (degrees); null if altitudes unknown. */
  elevationDeg: number | null;
  /** Relative bearing from `from`'s track (only when `from` has a track). */
  relativeBearingDeg: number | null;
  clock: number | null;
}

/** |closing| below this (kt) reads as 'steady'. */
export const TREND_DEADBAND_KT = 3;
/** ETA is only given when closing faster than this (kt). */
export const ETA_MIN_CLOSING_KT = 5;
/** CPA is only given when relative speed exceeds this (kt). */
export const CPA_MIN_REL_SPEED_KT = 1;

/** Horizontal velocity (east, north) in m/s; [0,0] when stationary; null when moving with unknown track. */
function velocityEN(k: Kinematic): [number, number] | null {
  const gs = k.gsKt;
  if (gs == null || !Number.isFinite(gs) || gs <= 0) return [0, 0];
  if (k.trackDeg == null || !Number.isFinite(k.trackDeg)) return null;
  const v = gs * MPS_PER_KT;
  const t = k.trackDeg * D2R;
  return [v * Math.sin(t), v * Math.cos(t)];
}

/** Full geometry between two kinematic bodies (either may be stationary). */
export function relativeGeometry(from: Kinematic, to: Kinematic): RelativeGeometry {
  const d = distanceM(from.pos, to.pos);
  const brg = bearingDeg(from.pos, to.pos);
  const recip = d === 0 ? normalizeDeg(brg + 180) : bearingDeg(to.pos, from.pos);
  const altKnown = from.altM != null && to.altM != null && Number.isFinite(from.altM) && Number.isFinite(to.altM);
  const altDiffFt = altKnown ? mToFt((to.altM as number) - (from.altM as number)) : null;
  const slant = altKnown ? slantRangeM(from.pos, from.altM as number, to.pos, to.altM as number) : null;
  const elevationDeg = altKnown && d + Math.abs((to.altM as number) - (from.altM as number)) > 0
    ? elevationAngleDeg(from.pos, from.altM as number, to.pos, to.altM as number)
    : null;

  // Line-of-sight direction at the midpoint: circular mean of the forward bearing and the
  // reversed reciprocal (removes meridian convergence over the baseline).
  const b1 = brg * D2R;
  const b2 = (recip + 180) * D2R;
  const losAz = Math.atan2(Math.sin(b1) + Math.sin(b2), Math.cos(b1) + Math.cos(b2));
  const ux = Math.sin(losAz);
  const uy = Math.cos(losAz);
  const rx = ux * d;
  const ry = uy * d;

  const vf = velocityEN(from);
  const vt = velocityEN(to);
  let closingKt: number | null = null;
  let trend: Trend = 'unknown';
  let etaSec: number | null = null;
  let cpa: RelativeGeometry['cpa'] = null;

  if (vf && vt) {
    const bothStationary = vf[0] === 0 && vf[1] === 0 && vt[0] === 0 && vt[1] === 0;
    if (bothStationary) {
      trend = 'steady';
    } else {
      const vx = vt[0] - vf[0];
      const vy = vt[1] - vf[1];
      // Range rate = d|r|/dt = r̂·v_rel; closing is its negative.
      const closingMps = d > 0 ? -(ux * vx + uy * vy) : Math.hypot(vx, vy);
      closingKt = mpsToKt(closingMps);
      trend = Math.abs(closingKt) < TREND_DEADBAND_KT ? 'steady' : closingKt > 0 ? 'closing' : 'opening';
      if (closingKt > ETA_MIN_CLOSING_KT && d > 0) etaSec = d / closingMps;
      const v2 = vx * vx + vy * vy;
      if (Math.sqrt(v2) > CPA_MIN_REL_SPEED_KT * MPS_PER_KT) {
        const tSec = Math.max(0, -(rx * vx + ry * vy) / v2);
        cpa = { tSec, distanceM: Math.hypot(rx + vx * tSec, ry + vy * tSec) };
      }
    }
  }

  let relBrg: number | null = null;
  let clock: number | null = null;
  if (from.trackDeg != null && Number.isFinite(from.trackDeg)) {
    relBrg = relativeBearingDeg(from.trackDeg, brg);
    clock = clockPosition(relBrg);
  }

  return {
    distanceM: d,
    distanceNm: d / M_PER_NM,
    bearingDeg: brg,
    reciprocalDeg: recip,
    altDiffFt,
    slantRangeM: slant,
    closingKt,
    trend,
    etaSec,
    cpa,
    elevationDeg,
    relativeBearingDeg: relBrg,
    clock,
  };
}

/** Unit converters. */
export const mToNm = (m: number) => m / M_PER_NM;
export const nmToM = (nm: number) => nm * M_PER_NM;
export const mToFt = (m: number) => m / M_PER_FT;
export const ftToM = (ft: number) => ft * M_PER_FT;
export const ktToMps = (kt: number) => kt * MPS_PER_KT;
export const mpsToKt = (mps: number) => mps / MPS_PER_KT;

/** Ring polygon (for range rings / accuracy circles) as [lon,lat] pairs closed. Default 64 segments. */
export function circlePolygon(center: LatLon, radiusM: number, segments = 64): [number, number][] {
  const n = Math.max(3, Math.floor(segments));
  const ring: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const p = destinationPoint(center, (360 * i) / n, radiusM);
    ring.push([p.lon, p.lat]);
  }
  ring.push([ring[0]![0], ring[0]![1]]);
  return ring;
}
